// 模拟数据文件
import moment from 'moment';

// 生成随机ID
const generateId = () => Math.random().toString(36).substr(2, 9);

// 生成随机日期
const randomDate = (start, end) => {
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
};

// 学科列表
const subjects = ['数学', '语文', '英语', '物理', '化学', '生物', '历史', '地理', '政治'];

// 知识点数据
const knowledgePoints = {
  '数学': ['函数与方程', '三角函数', '数列', '立体几何', '解析几何', '概率统计', '导数应用'],
  '语文': ['现代文阅读', '古诗词鉴赏', '文言文阅读', '作文写作', '语言文字运用'],
  '英语': ['阅读理解', '完形填空', '语法填空', '短文改错', '书面表达', '听力理解'],
  '物理': ['力学', '电学', '光学', '热学', '原子物理', '机械振动', '电磁感应'],
  '化学': ['有机化学', '无机化学', '物理化学', '化学实验', '化学计算', '化学平衡']
};

// 生成错题数据
const generateErrorQuestions = (count = 50) => {
  const errors = [];
  for (let i = 0; i < count; i++) {
    const subject = subjects[Math.floor(Math.random() * subjects.length)];
    const knowledgeList = knowledgePoints[subject] || ['基础知识'];
    const knowledge = knowledgeList[Math.floor(Math.random() * knowledgeList.length)];
    
    errors.push({
      id: generateId(),
      subject,
      knowledgePoint: knowledge,
      imageUrl: `/img/error-${(i % 10) + 1}.png`, // 假设有10张错题图片
      status: Math.random() > 0.3 ? 'unmastered' : 'mastered',
      createTime: randomDate(new Date(2024, 0, 1), new Date()),
      answer: `这道题考查的是${knowledge}的相关知识点。解题思路：1. 分析题目条件；2. 运用相关公式或定理；3. 计算得出结果；4. 检验答案的合理性。`,
      difficulty: Math.floor(Math.random() * 5) + 1, // 1-5难度
      practiceCount: Math.floor(Math.random() * 10),
      lastPracticeTime: randomDate(new Date(2024, 8, 1), new Date())
    });
  }
  return errors;
};

// 生成学生数据
const generateStudents = (count = 30) => {
  const students = [];
  const names = ['张三', '李四', '王五', '赵六', '钱七', '孙八', '周九', '吴十', '郑十一', '王十二'];
  
  for (let i = 0; i < count; i++) {
    students.push({
      id: generateId(),
      name: names[i % names.length] + (i > 9 ? i : ''),
      studentId: `2024${String(i + 1).padStart(3, '0')}`,
      grade: '高三',
      className: `${Math.floor(i / 10) + 1}班`,
      avatar: `/img/avatar-${(i % 8) + 1}.png`,
      totalErrors: Math.floor(Math.random() * 100) + 20,
      masteredErrors: Math.floor(Math.random() * 50) + 10,
      currentScore: Math.floor(Math.random() * 40) + 60,
      targetScore: Math.floor(Math.random() * 20) + 80,
      studyDays: Math.floor(Math.random() * 100) + 10,
      lastLoginTime: randomDate(new Date(2024, 8, 1), new Date())
    });
  }
  return students;
};

// 生成作业数据
const generateAssignments = (count = 20) => {
  const assignments = [];
  const titles = ['数学周测', '英语阅读练习', '物理实验报告', '化学计算题', '语文作文', '历史材料题'];
  
  for (let i = 0; i < count; i++) {
    const createTime = randomDate(new Date(2024, 7, 1), new Date());
    const dueTime = new Date(createTime.getTime() + Math.random() * 7 * 24 * 60 * 60 * 1000);
    
    assignments.push({
      id: generateId(),
      title: titles[i % titles.length] + ` ${i + 1}`,
      subject: subjects[Math.floor(Math.random() * subjects.length)],
      description: '请认真完成本次作业，注意解题步骤的完整性。',
      createTime,
      dueTime,
      status: Math.random() > 0.3 ? 'published' : 'draft',
      submissionCount: Math.floor(Math.random() * 25) + 5,
      totalStudents: 30,
      avgScore: Math.floor(Math.random() * 30) + 70
    });
  }
  return assignments;
};

// 生成学习活动数据
const generateStudyActivities = (count = 10) => {
  const activities = [];
  const types = ['login', 'study', 'practice', 'error', 'complete'];
  const titles = {
    login: '登录系统',
    study: '开始学习',
    practice: '专项练习',
    error: '添加错题',
    complete: '完成任务'
  };
  const descriptions = {
    login: '学生登录学习系统',
    study: '开始数学函数专题学习',
    practice: '完成英语阅读理解练习',
    error: '添加物理力学错题',
    complete: '完成今日学习任务'
  };
  
  for (let i = 0; i < count; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    activities.push({
      id: generateId(),
      type,
      title: titles[type],
      description: descriptions[type],
      time: moment().subtract(Math.floor(Math.random() * 12), 'hours').format('HH:mm')
    });
  }
  return activities.sort((a, b) => a.time.localeCompare(b.time));
};

// 生成学习任务数据
const generateTasks = (count = 8) => {
  const tasks = [];
  const taskTitles = [
    '完成数学函数练习',
    '背诵英语单词50个',
    '复习物理电学知识',
    '完成化学实验报告',
    '阅读语文课外文章',
    '练习历史材料分析',
    '完成地理地图题',
    '复习政治时事热点'
  ];
  
  for (let i = 0; i < count; i++) {
    tasks.push({
      id: generateId(),
      title: taskTitles[i % taskTitles.length],
      description: '请按时完成，注意质量',
      completed: Math.random() > 0.4,
      priority: Math.floor(Math.random() * 3) + 1, // 1-3优先级
      estimatedTime: Math.floor(Math.random() * 60) + 30, // 30-90分钟
      subject: subjects[Math.floor(Math.random() * subjects.length)]
    });
  }
  return tasks;
};

// 生成薄弱知识点数据
const generateWeakPoints = (count = 6) => {
  const weakPoints = [];
  const allKnowledge = Object.values(knowledgePoints).flat();
  
  for (let i = 0; i < count; i++) {
    weakPoints.push({
      id: generateId(),
      name: allKnowledge[i % allKnowledge.length],
      subject: subjects[Math.floor(Math.random() * subjects.length)],
      accuracy: Math.floor(Math.random() * 40) + 30, // 30-70%正确率
      errorCount: Math.floor(Math.random() * 20) + 5,
      practiceCount: Math.floor(Math.random() * 50) + 10,
      lastPracticeTime: randomDate(new Date(2024, 8, 1), new Date())
    });
  }
  return weakPoints.sort((a, b) => a.accuracy - b.accuracy);
};

// 生成学习提醒数据
const generateReminders = () => {
  return [
    {
      id: generateId(),
      type: 'study',
      title: '开始学习',
      time: '19:00',
      enabled: true,
      description: '每天晚上7点开始学习'
    },
    {
      id: generateId(),
      type: 'practice',
      title: '错题练习',
      time: '20:30',
      enabled: true,
      description: '每天晚上8点半练习错题'
    },
    {
      id: generateId(),
      type: 'review',
      title: '复习总结',
      time: '21:30',
      enabled: false,
      description: '每天晚上9点半复习总结'
    },
    {
      id: generateId(),
      type: 'rest',
      title: '休息提醒',
      time: '22:00',
      enabled: true,
      description: '每天晚上10点提醒休息'
    }
  ];
};

// 生成图表数据
const generateChartData = (days = 7) => {
  const dates = [];
  const studyTimes = [];
  const scores = [];
  
  for (let i = days - 1; i >= 0; i--) {
    const date = moment().subtract(i, 'days');
    dates.push(date.format('MM-DD'));
    studyTimes.push(Math.floor(Math.random() * 120) + 60); // 60-180分钟
    scores.push(Math.floor(Math.random() * 30) + 70); // 70-100分
  }
  
  return { dates, studyTimes, scores };
};

// 模拟API响应
export const mockApi = {
  // 登录相关
  '/auth/login': () => ({
    code: 200,
    message: '登录成功',
    data: {
      token: 'mock-token-' + Date.now(),
      userInfo: {
        id: generateId(),
        username: 'demo_user',
        role: 'student',
        name: '演示用户'
      }
    }
  }),

  // 学生相关API
  '/student/statistics': () => ({
    code: 200,
    data: {
      totalErrorQuestions: Math.floor(Math.random() * 50) + 20,
      currentScore: Math.floor(Math.random() * 30) + 70,
      targetScore: 90,
      studyDays: Math.floor(Math.random() * 50) + 10
    }
  }),

  '/student/progress': () => ({
    code: 200,
    data: {
      progressPercentage: Math.floor(Math.random() * 60) + 30,
      remainingDays: Math.floor(Math.random() * 30) + 10
    }
  }),

  '/student/today-tasks': () => ({
    code: 200,
    data: {
      tasks: generateTasks()
    }
  }),

  '/student/weak-points': () => ({
    code: 200,
    data: {
      weakPoints: generateWeakPoints()
    }
  }),

  '/student/recent-errors': () => ({
    code: 200,
    data: {
      errors: generateErrorQuestions(5)
    }
  }),

  '/student/error-book': () => {
    const errors = generateErrorQuestions(30);
    const totalErrors = errors.length;
    const masteredErrors = errors.filter(e => e.status === 'mastered').length;
    const unmasteredErrors = totalErrors - masteredErrors;
    
    return {
      code: 200,
      data: {
        list: errors.slice(0, 12), // 分页显示
        total: totalErrors,
        totalErrors,
        masteredErrors,
        unmasteredErrors
      }
    };
  },

  '/student/knowledge-points': () => ({
    code: 200,
    data: Object.entries(knowledgePoints).flatMap(([subject, points]) =>
      points.map(point => ({ label: point, value: point, subject }))
    )
  }),

  // 学生学习计划相关API
  '/student/current-plan': () => ({
    code: 200,
    data: {
      id: generateId(),
      title: '高考冲刺学习计划',
      description: '针对薄弱知识点制定的个性化学习计划',
      startDate: moment().subtract(15, 'days').format('YYYY-MM-DD'),
      endDate: moment().add(45, 'days').format('YYYY-MM-DD'),
      progress: Math.floor(Math.random() * 60) + 25,
      currentScore: Math.floor(Math.random() * 30) + 70,
      targetScore: Math.floor(Math.random() * 20) + 85,
      subjects: subjects.slice(0, 5).map(subject => ({
        name: subject,
        progress: Math.floor(Math.random() * 80) + 20,
        targetScore: Math.floor(Math.random() * 20) + 80
      })),
      weeklyTasks: generateTasks(7),
      status: 'active'
    }
  }),

  '/student/history-plans': () => ({
    code: 200,
    data: {
      list: Array.from({ length: 5 }, (_, i) => ({
        id: generateId(),
        title: `学习计划 ${i + 1}`,
        startDate: moment().subtract((i + 1) * 60, 'days').format('YYYY-MM-DD'),
        endDate: moment().subtract(i * 30, 'days').format('YYYY-MM-DD'),
        status: i === 0 ? 'active' : 'completed',
        progress: i === 0 ? Math.floor(Math.random() * 60) + 25 : 100,
        finalScore: i === 0 ? null : Math.floor(Math.random() * 30) + 70
      })),
      total: 5
    }
  }),

  '/student/study-stats': () => {
    const chartData = generateChartData(30);
    return {
      code: 200,
      data: {
        dailyStats: chartData.dates.map((date, index) => ({
          date,
          studyTime: chartData.studyTimes[index],
          errorCount: Math.floor(Math.random() * 10) + 2,
          practiceCount: Math.floor(Math.random() * 20) + 5,
          score: chartData.scores[index]
        })),
        summary: {
          totalStudyTime: chartData.studyTimes.reduce((a, b) => a + b, 0),
          avgDailyStudy: Math.floor(chartData.studyTimes.reduce((a, b) => a + b, 0) / chartData.studyTimes.length),
          totalErrors: Math.floor(Math.random() * 100) + 50,
          masteredErrors: Math.floor(Math.random() * 50) + 25,
          avgScore: Math.floor(chartData.scores.reduce((a, b) => a + b, 0) / chartData.scores.length)
        }
      }
    };
  },

  // 教师相关API
  '/teacher/info': () => ({
    code: 200,
    data: {
      id: generateId(),
      name: '张老师',
      subject: '数学',
      title: '高级教师',
      avatar: '/img/teacher-avatar.png',
      phone: '138****8888',
      email: 'teacher@school.com'
    }
  }),

  '/teacher/class-stats': () => ({
    code: 200,
    data: {
      totalStudents: 30,
      activeStudents: 28,
      avgScore: 78.5,
      totalAssignments: 15,
      pendingAssignments: 3
    }
  }),

  '/teacher/common-errors': () => ({
    code: 200,
    data: generateErrorQuestions(10)
  }),

  '/teacher/students': () => ({
    code: 200,
    data: generateStudents()
  }),

  '/teacher/recent-assignments': () => ({
    code: 200,
    data: generateAssignments(8)
  }),

  '/teacher/knowledge-points': () => ({
    code: 200,
    data: generateWeakPoints(10)
  }),

  '/teacher/score-analysis': () => {
    const chartData = generateChartData(14);
    return {
      code: 200,
      data: {
        chartData,
        summary: {
          avgScore: 78.5,
          improvement: 5.2,
          topStudent: '张三',
          weakestPoint: '函数与方程'
        }
      }
    };
  },

  // 教师作业管理API
  '/teacher/assignments': () => ({
    code: 200,
    data: {
      list: generateAssignments(15),
      total: 15,
      published: 12,
      draft: 3,
      expired: 2
    }
  }),

  // 家长相关API
  '/parent/children': () => ({
    code: 200,
    data: [
      {
        id: generateId(),
        name: '小明',
        grade: '高三',
        className: '1班',
        avatar: '/img/child-avatar-1.png',
        studentId: '202401001'
      },
      {
        id: generateId(),
        name: '小红',
        grade: '高二',
        className: '2班',
        avatar: '/img/child-avatar-2.png',
        studentId: '202401002'
      }
    ]
  }),

  '/parent/child/:id/overview': () => ({
    code: 200,
    data: {
      todayStudyTime: Math.floor(Math.random() * 120) + 60,
      studyTimeTrend: Math.floor(Math.random() * 20) - 10,
      errorCount: Math.floor(Math.random() * 10) + 5,
      errorTrend: Math.floor(Math.random() * 20) - 10,
      latestScore: Math.floor(Math.random() * 30) + 70,
      scoreTrend: Math.floor(Math.random() * 10) - 5,
      studyStreak: Math.floor(Math.random() * 30) + 5
    }
  }),

  '/parent/child/:id/current-plan': () => ({
    code: 200,
    data: {
      id: generateId(),
      title: '高考冲刺计划',
      startDate: moment().subtract(30, 'days').toDate(),
      endDate: moment().add(60, 'days').toDate(),
      progress: Math.floor(Math.random() * 60) + 20,
      currentScore: Math.floor(Math.random() * 30) + 70,
      targetScore: 90
    }
  }),

  '/parent/child/:id/study-reports': () => {
    const chartData = generateChartData(7);
    return {
      code: 200,
      data: {
        chartData,
        summary: {
          avgDailyStudy: Math.floor(Math.random() * 60) + 90,
          completedAssignments: Math.floor(Math.random() * 10) + 5,
          errorMasteryRate: Math.floor(Math.random() * 30) + 60
        }
      }
    };
  },

  '/parent/child/:id/today-study': () => ({
    code: 200,
    data: {
      status: ['active', 'completed', 'inactive'][Math.floor(Math.random() * 3)],
      activities: generateStudyActivities()
    }
  }),

  '/parent/child/:id/recent-errors': () => ({
    code: 200,
    data: generateErrorQuestions(6)
  }),

  '/parent/child/:id/reminders': () => ({
    code: 200,
    data: generateReminders()
  }),

  // 聊天历史API
  '/requestAnswerRecord': () => ({
    code: 200,
    data: {
      list: Array.from({ length: 10 }, (_, i) => ({
        id: generateId(),
        type: i % 2 === 0 ? 'user' : 'assistant',
        content: i % 2 === 0 ? `用户消息 ${i + 1}` : `助手回复 ${i + 1}`,
        timestamp: moment().subtract(i * 30, 'minutes').toDate(),
        avatar: i % 2 === 0 ? '/img/user-avatar.png' : '/img/assistant-avatar.png'
      })),
      total: 10
    }
  }),

  // 用户相关API
  '/user/profile': () => ({
    code: 200,
    data: {
      id: generateId(),
      username: 'demo_user',
      name: '演示用户',
      email: 'demo@example.com',
      phone: '138****8888',
      avatar: '/img/def_img.png',
      role: 'student',
      grade: '高三',
      className: '1班',
      school: '示例中学',
      joinDate: '2024-01-01'
    }
  }),

  '/user/profile-stats': () => ({
    code: 200,
    data: {
      loginDays: Math.floor(Math.random() * 50) + 20,
      studyHours: Math.floor(Math.random() * 100) + 50,
      errorCount: Math.floor(Math.random() * 50) + 20,
      studentCount: Math.floor(Math.random() * 30) + 20
    }
  }),

  // 用户设置相关API
  '/user/preferences': () => ({
    code: 200,
    data: {
      theme: 'light',
      language: 'zh-CN',
      notifications: {
        email: true,
        push: true,
        sms: false
      },
      privacy: {
        showProfile: true,
        showActivity: false,
        allowContact: true
      },
      study: {
        dailyGoal: 120, // 分钟
        reminderTime: '19:00',
        autoSave: true,
        showProgress: true
      }
    }
  }),

  '/user/login-records': () => ({
    code: 200,
    data: {
      list: Array.from({ length: 20 }, (_, i) => ({
        id: generateId(),
        loginTime: moment().subtract(i, 'days').subtract(Math.floor(Math.random() * 12), 'hours').toDate(),
        ip: `192.168.1.${Math.floor(Math.random() * 255)}`,
        device: ['Windows PC', 'iPhone', 'Android', 'iPad', 'Mac'][Math.floor(Math.random() * 5)],
        location: ['北京', '上海', '广州', '深圳', '杭州'][Math.floor(Math.random() * 5)],
        status: 'success'
      })),
      total: 20
    }
  }),

  // 学生创建计划API
  '/student/create-plan': () => ({
    code: 200,
    message: '学习计划创建成功',
    data: {
      id: generateId(),
      title: '新建学习计划',
      status: 'draft'
    }
  }),

  // 密码修改API
  '/user/change-password': () => ({
    code: 200,
    message: '密码修改成功'
  }),

  // 错题状态更新API (PUT)
  '/student/error-book/:id/status': () => ({
    code: 200,
    message: '错题状态更新成功'
  }),

  // 批量掌握错题API (PUT)
  '/student/error-book/batch-mastered': () => ({
    code: 200,
    message: '批量标记掌握成功',
    data: {
      updatedCount: Math.floor(Math.random() * 10) + 1
    }
  }),

  // 批量打印错题API (POST)
  '/student/error-book/batch-print': () => ({
    code: 200,
    message: '打印任务已提交',
    data: {
      printId: generateId(),
      estimatedTime: '2-3分钟'
    }
  })
};

// 默认导出所有数据
export default {
  subjects,
  knowledgePoints,
  generateErrorQuestions,
  generateStudents,
  generateAssignments,
  generateStudyActivities,
  generateTasks,
  generateWeakPoints,
  generateReminders,
  generateChartData,
  mockApi
};
